import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Chip,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Alert,
  Button,
} from "@mui/material";
import {
  Approval,
  ApprovalStatus,
  ApprovalType,
  ApprovalPriority,
} from "./approvalsTypes";
import ApprovalResponseActions from "./ApprovalResponseActions";
import ApprovalDiscussion from "./ApprovalDiscussion";

interface ApprovalCardProps {
  approval: Approval;
  onEdit?: () => void;
  onDelete?: () => void;
  showActions?: boolean;
  projectId: string;
  contractorId: string;
  currentUserId?: string;
  currentUserRole?: "contractor" | "client";
  currentUserName?: string;
  onStatusChanged?: (newStatus: ApprovalStatus) => void;
}

const ApprovalCard: React.FC<ApprovalCardProps> = ({
  approval,
  onEdit,
  onDelete,
  showActions = true,
  projectId,
  contractorId,
  currentUserId = "",
  currentUserRole = "contractor",
  currentUserName = "You",
  onStatusChanged,
}) => {
  const [status, setStatus] = useState<ApprovalStatus>(approval.status);
  const getStatusColor = (
    status: ApprovalStatus,
  ): "default" | "success" | "error" | "warning" | "info" => {
    switch (status) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "discussion":
        return "warning";
      case "pending":
      default:
        return "info";
    }
  };

  const getTypeLabel = (type: ApprovalType) => {
    return type === "change_order" ? "Change Order" : "Material Selection";
  };

  const getPriorityColor = (priority: ApprovalPriority) => {
    return priority === "urgent" ? "error" : "default";
  };

  const getLineItemsTotal = (): number => {
    if (!approval.pricing_breakdown) return 0;
    return approval.pricing_breakdown.reduce(
      (sum: number, item) => sum + item.quantity * item.unit_price,
      0,
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">{approval.title}</Typography>
            <Chip
              label={getTypeLabel(approval.type)}
              size="small"
              variant="outlined"
            />
            <Chip
              label={status}
              size="small"
              color={getStatusColor(status)}
              variant="filled"
            />
            <Chip
              label={approval.priority}
              size="small"
              color={getPriorityColor(approval.priority)}
              variant="filled"
            />
          </Box>
        }
        subheader={formatDate(approval.timestamp)}
      />
      <CardContent>
        <Stack spacing={2}>
          {/* Description */}
          {approval.description && (
            <>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Description
                </Typography>
                <Typography variant="body2">{approval.description}</Typography>
              </Box>
              <Divider />
            </>
          )}

          {/* Pricing Breakdown */}
          {approval.pricing_breakdown &&
            approval.pricing_breakdown.length > 0 && (
              <>
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    sx={{ mb: 1 }}
                  >
                    Pricing Breakdown
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {approval.pricing_breakdown.map((item, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{item.description}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">
                            ${item.unit_price.toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            ${(item.quantity * item.unit_price).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Box sx={{ textAlign: "right", mt: 1 }}>
                    <Typography variant="subtitle2">
                      Total: <strong>${getLineItemsTotal().toFixed(2)}</strong>
                    </Typography>
                  </Box>
                </Box>
                <Divider />
              </>
            )}

          {/* Timeline Impact */}
          {approval.timeline_impact && (
            <Box>
              <Typography variant="body2" color="textSecondary">
                Timeline Impact:{" "}
                <strong>{approval.timeline_impact} days</strong>
              </Typography>
            </Box>
          )}

          {/* Client Response */}
          {approval.client_response && (
            <>
              <Divider />
              <Alert severity="info" icon={false}>
                <Typography variant="subtitle2">Client Response</Typography>
                <Typography variant="body2">
                  {approval.client_response}
                </Typography>
                {approval.client_response_date && (
                  <Typography variant="caption" color="textSecondary">
                    {formatDate(approval.client_response_date)}
                  </Typography>
                )}
              </Alert>
            </>
          )}

          {/* Actions */}
          {showActions && (
            <>
              <Divider />

              {/* Response Actions (Approve/Reject/Discuss) */}
              <ApprovalResponseActions
                approval={{ ...approval, status }}
                projectId={projectId}
                contractorId={contractorId}
                userId={currentUserId}
                userRole={currentUserRole}
                onStatusChanged={(newStatus) => {
                  setStatus(newStatus);
                  onStatusChanged?.(newStatus);
                }}
              />

              {/* Discussion/Comments Section */}
              {status === "discussion" && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Discussion Thread
                  </Typography>
                  <ApprovalDiscussion
                    approvalId={approval.id}
                    userId={currentUserId || contractorId}
                    userRole={currentUserRole}
                    userName={currentUserName}
                    showCommentForm={status === "discussion"}
                  />
                </>
              )}

              {/* Edit/Delete Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "flex-end",
                  mt: 2,
                }}
              >
                {onEdit && status === "pending" && (
                  <Button size="small" onClick={onEdit}>
                    Edit
                  </Button>
                )}
                {onDelete && status === "pending" && (
                  <Button size="small" color="error" onClick={onDelete}>
                    Delete
                  </Button>
                )}
              </Box>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ApprovalCard;
